import { useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { map } from 'lodash';
import LightGallery from 'lightgallery/react';
import lgThumbnail from 'lightgallery/plugins/thumbnail';

import 'lightgallery/css/lightgallery.css';
import 'lightgallery/css/lg-zoom.css';
import 'lightgallery/css/lg-thumbnail.css';

import { ImageWrapperContainer } from './ImageGalleryImage.styles';
import {
  GalleryContainer,
  GalleryImage,
  ThumbnailsContainer,
  ThumbnailItem,
  ThumbnailCounter,
  AdditionalImageCount
} from './ImageGallery.styles';
import ImageNavigation from './components/ImageNavigation';
import { LIGHT_GALLERY_KEY } from '../../constants';

const THUMBNAIL_LIMIT = 9;

function ImageGallery({ images, listingRework, isDashboard }) {
  const imageCount = images.length;
  const [currentImage, setCurrentImage] = useState(images[0]);
  const lightGalleryRef = useRef(null);

  const onInit = useCallback((detail) => {
    if (detail) {
      lightGalleryRef.current = detail.instance;
    }
  }, []);

  const handleImageChange = (direction) => {
    const currentIndex = images.findIndex((img) => img === currentImage);
    let newIndex;

    if (direction === 'back') {
      newIndex = currentIndex > 0 ? currentIndex - 1 : images.length - 1;
    } else {
      newIndex = currentIndex < images.length - 1 ? currentIndex + 1 : 0;
    }

    setCurrentImage(images[newIndex]);
  };

  const handleThumbnailClick = (image) => {
    setCurrentImage(image);
  };

  const handleMainImageClick = (index) => {
    if (lightGalleryRef.current) {
      lightGalleryRef.current.openGallery(index);
    }
  };

  const currentIndex = images.findIndex((img) => img === currentImage);

  if (!images || images.length === 0) {
    return (
      <ImageWrapperContainer style={{ position: 'relative' }}>
        <img src="/images/logo-grey-pipe.svg" alt="Listing" />
      </ImageWrapperContainer>
    );
  }

  return (
    <GalleryContainer>
      <div>
        <ImageWrapperContainer isDashboard={isDashboard}>
          <LightGallery
            elementClassNames="custom-light-gallery-class"
            plugins={[lgThumbnail]}
            licenseKey={LIGHT_GALLERY_KEY}
            onInit={onInit}
          >
            {map(images, (img) => (
              <a
                href={img.image}
                key={img.image}
                className={
                  currentImage === img ? 'active-image' : 'hidden-gallery-item'
                }
                style={{
                  display: currentImage === img ? 'block' : 'none',
                }}
                onClick={() => handleMainImageClick(currentIndex)}
              >
                <GalleryImage
                  src={img.image}
                  alt={img.original_file_name}
                  rework={listingRework}
                  onLoad={(e) => {
                    e.target.style.display = 'block';
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </a>
            ))}
          </LightGallery>

          {images.length > 1 && (
            <ImageNavigation
              currentIndex={currentIndex}
              total={imageCount}
              onPrevious={(e) => {
                e.stopPropagation();
                handleImageChange('back');
              }}
              onNext={(e) => {
                e.stopPropagation();
                handleImageChange('forward');
              }}
            />
          )}
        </ImageWrapperContainer>
      </div>

      <ThumbnailsContainer>
        {map(images, (img, index) =>
          index < THUMBNAIL_LIMIT ? (
            <ThumbnailItem
              key={img.image}
              onClick={() => handleThumbnailClick(img)}
              active={currentImage === img}
              rework={listingRework}
            >
              <img
                src={img.image}
                alt={img.original_file_name}
                onLoad={(e) => {
                  e.target.style.display = 'block';
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </ThumbnailItem>
          ) : null,
        )}
        {imageCount > THUMBNAIL_LIMIT ? (
          <ThumbnailCounter rework={listingRework}>
            <AdditionalImageCount bold>
              {`+${imageCount - THUMBNAIL_LIMIT}`}
            </AdditionalImageCount>
          </ThumbnailCounter>
        ) : null}
      </ThumbnailsContainer>
    </GalleryContainer>
  );
}

ImageGallery.propTypes = {
  images: PropTypes.arrayOf(
    PropTypes.shape({
      number: PropTypes.number,
      image: PropTypes.string,
      original_file_name: PropTypes.string,
    }),
  ),
  listingRework: PropTypes.bool,
  isDashboard: PropTypes.bool,
};

export default ImageGallery;
